/*
 * ToastContext.tsx
 * Contexte global pour l'affichage de notifications toast.
 * Expose showToast(message, type) utilisable depuis n'importe quel composant.
 * Écoute également l'événement natif 'app:toast' émis depuis apiFetch.ts
 * pour permettre l'affichage de toasts depuis des services sans accès au contexte React.
 */

// Import React
import React from 'react';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faCheckCircle,
	faExclamationTriangle,
	faInfoCircle,
	faTimes,
	faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';

// Types
export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
	id: number;
	message: string;
	type: ToastType;
}

interface ToastContextValue {
	showToast: (message: string, type?: ToastType) => void;
}

// Création du contexte
export const ToastContext = React.createContext<ToastContextValue>({
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	showToast: () => {},
});

// Icônes selon le type
const TOAST_ICONS: Record<ToastType, React.ReactNode> = {
	success: <FontAwesomeIcon icon={faCheckCircle} />,
	error: <FontAwesomeIcon icon={faTimesCircle} />,
	warning: <FontAwesomeIcon icon={faExclamationTriangle} />,
	info: <FontAwesomeIcon icon={faInfoCircle} />,
};

// Classes de couleur selon le type
const TOAST_CLASSES: Record<ToastType, string> = {
	success: 'bg-green-50 text-green-800 border border-green-300',
	error: 'bg-red-50 text-red-800 border border-red-300',
	warning: 'bg-amber-50 text-amber-800 border border-amber-300',
	info: 'bg-blue-50 text-blue-800 border border-blue-300',
};

// Durée d'affichage en ms
const TOAST_DURATION = 4000;

// Composant Provider
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	// Hooks de State
	const [toasts, setToasts] = React.useState<Toast[]>([]);
	const toastIdRef = React.useRef(0);

	// Afficher un toast
	const showToast = React.useCallback(
		(message: string, type: ToastType = 'info') => {
			const id = ++toastIdRef.current;
			setToasts((prev) => [...prev, { id, message, type }]);

			// Suppression automatique après TOAST_DURATION ms
			setTimeout(() => {
				setToasts((prev) => prev.filter((t) => t.id !== id));
			}, TOAST_DURATION);
		},
		[]
	);

	// Suppression manuelle d'un toast
	const dismissToast = (id: number) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	};

	// Écoute de l'événement natif 'app:toast' émis depuis apiFetch.ts
	React.useEffect(() => {
		const handleNativeToast = (e: Event) => {
			const { message, type } = (
				e as CustomEvent<{ message: string; type: ToastType }>
			).detail;
			showToast(message, type);
		};

		window.addEventListener('app:toast', handleNativeToast);
		return () => window.removeEventListener('app:toast', handleNativeToast);
	}, [showToast]);

	// Rendu du composant
	return (
		<ToastContext.Provider value={{ showToast }}>
			{children}

			{/* Conteneur des toasts, positionné en haut à droite */}
			<div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
				{toasts.map((toast) => (
					<div
						key={toast.id}
						className={`
							flex items-center gap-3 px-4 py-3 rounded-lg shadow-md
							pointer-events-auto min-w-64 max-w-sm
							animate-fade-in-down
							${TOAST_CLASSES[toast.type]}
						`}
					>
						{/* Icône */}
						<span className="text-lg flex-shrink-0">
							{TOAST_ICONS[toast.type]}
						</span>

						{/* Message */}
						<span className="flex-1 text-sm font-medium">{toast.message}</span>

						{/* Bouton de fermeture */}
						<button
							onClick={() => dismissToast(toast.id)}
							className="flex-shrink-0 opacity-80 hover:opacity-100 transition-opacity"
						>
							<FontAwesomeIcon icon={faTimes} className="text-sm" />
						</button>
					</div>
				))}
			</div>
		</ToastContext.Provider>
	);
};

// Hook utilitaire
export const useToast = () => React.useContext(ToastContext);
